// One plain HTML page (`HTMLWriter`, SuiteCompile/HTMLWriter.swift): a journal post, or the journal, to paste into
// Substack or another editor. Also `HTMLLists`, shared with the EPUB writer, and the footnote ids both use.
import { isCFAlphanumeric, isNumbered, listStyleOf, startsList, verseLines, type Block, type Inline, type ListStyle } from '@writing-suite/core';
import { BOOK_PAGE_KINDS, bylineName, heading, isPost } from './manuscript.ts';
import type { CompiledManuscript } from './types.ts';
import { BookMatter } from '@writing-suite/core';
import { xmlEscape as esc } from './xml.ts';

/** `EpubWriter.noteID`: a footnote's id, scoped by section so notes numbered alike in one file stay unique. */
export function noteID(id: string, scope: string): string {
  let safe = '';
  for (const ch of id) safe += isCFAlphanumeric(ch.codePointAt(0)!) || ch === '-' || ch === '_' ? ch : '_';
  return scope === '' ? `fn-${safe}` : `fn-${scope}-${safe}`;
}

/** `HTMLLists.tags`: the tags between `previous` and `block` (null after the last). `inline` styles markers inline. */
export function listTags(previous: Block | null, block: Block | null, inline: boolean): string {
  let out = '';
  const opens = block ? startsList(block, previous) : false;
  const prevStyle = previous ? listStyleOf(previous) : null;
  if (prevStyle !== null && (opens || !block || listStyleOf(block) === null)) out += isNumbered(prevStyle) ? '</ol>' : '</ul>';
  if (opens && block && block.kind.type === 'listItem') out += openList(block.kind.style, block.kind.number, inline);
  return out;
}

function openList(style: ListStyle, start: number, inline: boolean): string {
  let marker: string | null = null;
  switch (style) {
    case 'circle': marker = 'circle'; break;
    case 'dash': marker = inline ? "'\\2013  '" : 'dash'; break;
    case 'decimalParen': marker = inline ? null : 'paren'; break;
    default: marker = null;
  }
  const attribute = marker !== null ? (inline ? ` style="list-style-type: ${marker}"` : ` class="${marker}"`) : '';
  if (!isNumbered(style)) return `<ul${attribute}>`;
  const type = style === 'lowerAlpha' ? ' type="a"' : style === 'upperAlpha' ? ' type="A"' : style === 'lowerRoman' ? ' type="i"'
    : style === 'upperRoman' ? ' type="I"' : '';
  return `<ol${type}${start === 1 ? '' : ` start="${start}"`}${attribute}>`;
}

/** `HTMLWriter.write`. */
export function writeHTML(m: CompiledManuscript): string {
  const body = htmlBody(m);
  const title = isPost(m) ? (m.sections.find((s) => s.fragmentID !== null && s.matter === null)?.title ?? m.title) : m.title;
  const language = m.language === '' ? 'en' : m.language;
  const r = m.preset.render;
  return `<!DOCTYPE html>
<html lang="${esc(language)}">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${esc(title)}</title>
<style>
body { max-width: 38em; margin: 2.5em auto; padding: 0 1.25em; font: 18px/${swiftNumber(r.lineSpacing)} Georgia, serif; color: #222; }
p { margin: 0 0 ${swiftNumber(r.paragraphSpacing)}pt; }
h1, h2 { font-weight: normal; line-height: 1.25; }
hr { border: 0; text-align: center; margin: 1.5em 0; }
hr::after { content: "${r.sceneBreak.replaceAll('"', '\\"')}"; }
blockquote { margin: 1em 2em; }
.sc { font-variant: small-caps; }
.notes { font-size: 0.85em; border-top: 1px solid #ccc; margin-top: 2em; padding-top: 0.5em; }
</style>
</head>
<body>
<article>
${body}
</article>
</body>
</html>
`;
}

/** A Double interpolated in Swift: "1.5", "12.0". */
export const swiftNumber = (x: number) => (Number.isInteger(x) ? `${x}.0` : String(x));

/** `HTMLWriter.body`: the article's contents alone ("Copy for Substack"). */
export function htmlBody(m: CompiledManuscript): string {
  const r = m.preset.render;
  const out: string[] = [];
  if (!isPost(m)) {
    out.push(`<h1>${esc(m.title)}</h1>`);
    if (m.author) out.push(`<p>by ${esc(bylineName(m.author))}</p>`);
  }
  m.sections.forEach((section, index) => {
    const scope = String(index + 1);
    switch (section.role) {
      case 'part': {
        const lines = heading(r.partHeading, section);
        if (lines.length) out.push(`<h1>${esc(lines.join(': '))}</h1>`);
        break;
      }
      case 'chapter': {
        const lines = heading(r.chapterHeading, section);
        const tag = isPost(m) ? 'h1' : 'h2';
        if (lines.length) out.push(`<${tag}>${esc(lines.join(': '))}</${tag}>`);
        break;
      }
      case 'front-matter': case 'back-matter':
        if (section.matter !== null && BookMatter.isGenerated(section.matter)) return;
        if (section.matter === null || !BOOK_PAGE_KINDS.has(section.matter)) out.push(`<h2>${esc(section.title)}</h2>`);
        break;
      default:
        if (section.followsScene) out.push('<hr>');
    }
    const notes: string[] = [];
    let previous: Block | null = null;
    for (const block of section.blocks) {
      const content = htmlInlines(block.inlines, scope);
      const tags = listTags(previous, block, true);
      if (tags) out.push(tags);
      previous = block;
      switch (block.kind.type) {
        case 'sceneBreak': out.push('<hr>'); break;
        case 'paragraph': {
          const align = block.alignment ? ` style="text-align: ${block.alignment}"` : '';
          out.push(`<p${align}>${content}</p>`);
          break;
        }
        case 'listItem': out.push(`<li>${content}</li>`); break;
        case 'blockQuote': case 'epigraph': out.push(`<blockquote><p>${content}</p></blockquote>`); break;
        case 'verse': {
          // Pasted HTML keeps no stylesheet (Substack strips classes): lines by `<br>`, steps as em spaces.
          const align = block.alignment ? ` style="text-align: ${block.alignment}"` : '';
          const lines = verseLines(block).map((line) =>
            '&emsp;'.repeat(block.alignment === null ? line.indent : 0) + htmlInlines(line.inlines, scope));
          out.push(`<p class="verse"${align}>${lines.join('<br>')}</p>`);
          break;
        }
        case 'footnote': notes.push(`<li id="${noteID(block.kind.id, scope)}">${content}</li>`); break;
      }
    }
    const tags = listTags(previous, null, true);
    if (tags) out.push(tags);
    if (notes.length) out.push(`<ol class="notes">\n${notes.join('\n')}\n</ol>`);
  });
  if (r.endMarker !== '') out.push(`<p>${esc(r.endMarker)}</p>`);
  return out.join('\n');
}

function htmlInlines(inlines: Inline[], scope: string): string {
  return inlines.map((inline) => {
    switch (inline.type) {
      case 'text': {
        let s = esc(inline.text);
        if (inline.style.smallCaps) s = `<span class="sc">${s}</span>`;
        if (inline.style.underline) s = `<u>${s}</u>`;
        if (inline.style.bold) s = `<strong>${s}</strong>`;
        if (inline.style.italic) s = `<em>${s}</em>`;
        return s;
      }
      case 'lineBreak': return '<br>';
      case 'footnoteReference': return `<sup><a href="#${noteID(inline.id, scope)}">${esc(inline.id)}</a></sup>`;
      case 'linkStart': return `<a href="${esc(inline.address)}">`;
      case 'linkEnd': return '</a>';
    }
  }).join('');
}
